import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { inboxService } from "@/services/inboxService";
import { Conversation, Message, FormField } from "@/types/inbox.types";

// Constants
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_KEY = "inbox_conversations_cache";
const CACHE_TIMESTAMP_KEY = "inbox_conversations_timestamp";

// Types
export interface InboxState {
  // Conversations
  conversations: Conversation[];
  isLoadingConversations: boolean;
  conversationsError: Error | null;
  conversationsLastFetch: number | null;

  // Selected conversation details
  selectedConversation: Conversation | null;
  selectedMessages: Message[];
  selectedFormFields: FormField[];
  isLoadingConversationDetails: boolean;
  conversationDetailsError: Error | null;
  conversationDetailsCache: ConversationDetailsCache;

  // Global state
  isInitialized: boolean;
  organizationId: string | null;
}

interface ConversationDetailsCache {
  [conversationId: string]: {
    conversation: Conversation;
    messages: Message[];
    formFields: FormField[];
    timestamp: number;
  };
}

export interface InboxContextType extends InboxState {
  refreshConversations: () => Promise<void>;
  refreshConversationDetails: (conversationId: string) => Promise<void>;
  selectConversation: (conversationId: string | null) => void;
  updateConversationStatus: (
    conversationId: string,
    status: string,
  ) => Promise<void>;
  invalidateConversations: () => void;
  invalidateConversationDetails: (conversationId: string) => void;
  invalidateAll: () => void;
  reset: () => void;
  hasError: boolean;
  isLoading: boolean;
}

// Actions
type InboxAction =
  | { type: "SET_LOADING_CONVERSATIONS"; payload: boolean }
  | { type: "SET_CONVERSATIONS"; payload: Conversation[] }
  | { type: "SET_CONVERSATIONS_ERROR"; payload: Error | null }
  | { type: "SET_CONVERSATIONS_LAST_FETCH"; payload: number | null }
  | { type: "ADD_CONVERSATION"; payload: Conversation }
  | {
      type: "UPDATE_CONVERSATION";
      payload: Partial<Conversation> & { id: string };
    }
  | { type: "DELETE_CONVERSATION"; payload: string }
  | { type: "SET_SELECTED_CONVERSATION"; payload: Conversation | null }
  | { type: "SET_LOADING_CONVERSATION_DETAILS"; payload: boolean }
  | { type: "SET_SELECTED_MESSAGES"; payload: Message[] }
  | { type: "ADD_MESSAGE"; payload: Message }
  | { type: "UPDATE_MESSAGE"; payload: Partial<Message> & { id: string } }
  | { type: "SET_SELECTED_FORM_FIELDS"; payload: FormField[] }
  | { type: "SET_CONVERSATION_DETAILS_ERROR"; payload: Error | null }
  | {
      type: "CACHE_CONVERSATION_DETAILS";
      payload: {
        conversationId: string;
        conversation: Conversation;
        messages: Message[];
        formFields: FormField[];
      };
    }
  | {
      type: "LOAD_CACHED_CONVERSATION_DETAILS";
      payload: {
        conversation: Conversation;
        messages: Message[];
        formFields: FormField[];
      };
    }
  | {
      type: "UPDATE_CACHED_MESSAGES";
      payload: { conversationId: string; messages: Message[] };
    }
  | { type: "SET_ORGANIZATION_ID"; payload: string | null }
  | { type: "SET_INITIALIZED"; payload: boolean }
  | { type: "RESET" };

// Helper functions
const createDefaultState = (): InboxState => ({
  conversations: [],
  isLoadingConversations: false,
  conversationsError: null,
  conversationsLastFetch: null,
  selectedConversation: null,
  selectedMessages: [],
  selectedFormFields: [],
  isLoadingConversationDetails: false,
  conversationDetailsError: null,
  conversationDetailsCache: {},
  isInitialized: false,
  organizationId: null,
});

const isDataStale = (
  lastFetch: number | null,
  ttl: number = CACHE_TTL,
): boolean => {
  if (!lastFetch) return true;
  return Date.now() - lastFetch > ttl;
};

// Cache utilities
const saveConversationsToCache = (conversations: Conversation[]) => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(conversations));
    sessionStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error("Error saving conversations to cache:", error);
  }
};

const loadConversationsFromCache = (): {
  data: Conversation[] | null;
  timestamp: number | null;
} => {
  try {
    const data = sessionStorage.getItem(CACHE_KEY);
    const timestamp = sessionStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (data && timestamp) {
      return {
        data: JSON.parse(data),
        timestamp: parseInt(timestamp, 10),
      };
    }
  } catch (error) {
    console.error("Error loading conversations from cache:", error);
  }

  return { data: null, timestamp: null };
};

// Reducer
function inboxReducer(state: InboxState, action: InboxAction): InboxState {
  switch (action.type) {
    case "SET_LOADING_CONVERSATIONS":
      return { ...state, isLoadingConversations: action.payload };

    case "SET_CONVERSATIONS":
      return {
        ...state,
        conversations: action.payload,
        conversationsError: null,
      };

    case "SET_CONVERSATIONS_ERROR":
      return { ...state, conversationsError: action.payload };

    case "SET_CONVERSATIONS_LAST_FETCH":
      return { ...state, conversationsLastFetch: action.payload };

    case "ADD_CONVERSATION":
      return {
        ...state,
        conversations: [action.payload, ...state.conversations],
      };

    case "UPDATE_CONVERSATION":
      return {
        ...state,
        conversations: state.conversations.map((conv) =>
          conv.id === action.payload.id ? { ...conv, ...action.payload } : conv,
        ),
        selectedConversation:
          state.selectedConversation?.id === action.payload.id
            ? { ...state.selectedConversation, ...action.payload }
            : state.selectedConversation,
      };

    case "DELETE_CONVERSATION":
      return {
        ...state,
        conversations: state.conversations.filter(
          (conv) => conv.id !== action.payload,
        ),
        selectedConversation:
          state.selectedConversation?.id === action.payload
            ? null
            : state.selectedConversation,
      };

    case "SET_SELECTED_CONVERSATION":
      return { ...state, selectedConversation: action.payload };

    case "SET_LOADING_CONVERSATION_DETAILS":
      return { ...state, isLoadingConversationDetails: action.payload };

    case "SET_SELECTED_MESSAGES":
      return { ...state, selectedMessages: action.payload };

    case "ADD_MESSAGE":
      const updatedMessages = [...state.selectedMessages, action.payload];
      const conversationId = state.selectedConversation?.id;

      return {
        ...state,
        selectedMessages: updatedMessages,
        // Update cache if we have a selected conversation
        conversationDetailsCache:
          conversationId && state.conversationDetailsCache[conversationId]
            ? {
                ...state.conversationDetailsCache,
                [conversationId]: {
                  ...state.conversationDetailsCache[conversationId],
                  messages: updatedMessages,
                  timestamp: Date.now(),
                },
              }
            : state.conversationDetailsCache,
      };

    case "UPDATE_MESSAGE":
      const updatedMessagesForUpdate = state.selectedMessages.map((msg) =>
        msg.id === action.payload.id ? { ...msg, ...action.payload } : msg,
      );
      const conversationIdForUpdate = state.selectedConversation?.id;

      return {
        ...state,
        selectedMessages: updatedMessagesForUpdate,
        // Update cache if we have a selected conversation
        conversationDetailsCache:
          conversationIdForUpdate &&
          state.conversationDetailsCache[conversationIdForUpdate]
            ? {
                ...state.conversationDetailsCache,
                [conversationIdForUpdate]: {
                  ...state.conversationDetailsCache[conversationIdForUpdate],
                  messages: updatedMessagesForUpdate,
                  timestamp: Date.now(),
                },
              }
            : state.conversationDetailsCache,
      };

    case "SET_SELECTED_FORM_FIELDS":
      return { ...state, selectedFormFields: action.payload };

    case "SET_CONVERSATION_DETAILS_ERROR":
      return { ...state, conversationDetailsError: action.payload };

    case "CACHE_CONVERSATION_DETAILS":
      return {
        ...state,
        conversationDetailsCache: {
          ...state.conversationDetailsCache,
          [action.payload.conversationId]: {
            conversation: action.payload.conversation,
            messages: action.payload.messages,
            formFields: action.payload.formFields,
            timestamp: Date.now(),
          },
        },
      };

    case "LOAD_CACHED_CONVERSATION_DETAILS":
      return {
        ...state,
        selectedConversation: action.payload.conversation,
        selectedMessages: action.payload.messages,
        selectedFormFields: action.payload.formFields,
      };

    case "UPDATE_CACHED_MESSAGES":
      return {
        ...state,
        conversationDetailsCache: {
          ...state.conversationDetailsCache,
          [action.payload.conversationId]: {
            ...state.conversationDetailsCache[action.payload.conversationId],
            messages: action.payload.messages,
            timestamp: Date.now(),
          },
        },
      };

    case "SET_ORGANIZATION_ID":
      return { ...state, organizationId: action.payload };

    case "SET_INITIALIZED":
      return { ...state, isInitialized: action.payload };

    case "RESET":
      return createDefaultState();

    default:
      return state;
  }
}

// Context
const InboxContext = createContext<InboxContextType | undefined>(undefined);

// Provider
export function InboxProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(inboxReducer, createDefaultState());
  const { organization } = useAuth();
  const { toast } = useToast();
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Cleanup function
  const cleanup = useCallback(() => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
  }, []);

  // Initialize organization
  useEffect(() => {
    if (organization?.id !== state.organizationId) {
      dispatch({
        type: "SET_ORGANIZATION_ID",
        payload: organization?.id || null,
      });
      if (organization?.id) {
        dispatch({ type: "SET_INITIALIZED", payload: true });
      }
    }
  }, [organization?.id, state.organizationId]);

  // Fetch conversations
  const fetchConversations = useCallback(
    async (useCache = true): Promise<void> => {
      if (!state.organizationId) {
        return;
      }

      // Check cache first
      if (useCache) {
        const { data: cachedData, timestamp } = loadConversationsFromCache();
        if (cachedData && timestamp && !isDataStale(timestamp)) {
          dispatch({ type: "SET_CONVERSATIONS", payload: cachedData });
          dispatch({
            type: "SET_CONVERSATIONS_LAST_FETCH",
            payload: timestamp,
          });
          return;
        }
      }

      // Create abort controller
      const abortController = new AbortController();
      const fetchKey = `conversations-${Date.now()}`;
      abortControllersRef.current.set(fetchKey, abortController);

      try {
        dispatch({ type: "SET_LOADING_CONVERSATIONS", payload: true });
        dispatch({ type: "SET_CONVERSATIONS_ERROR", payload: null });

        const { data, error } = await inboxService.getConversations();

        if (abortController.signal.aborted) {
          return;
        }

        if (error) throw error;

        const conversations = data || [];
        dispatch({ type: "SET_CONVERSATIONS", payload: conversations });
        dispatch({ type: "SET_CONVERSATIONS_LAST_FETCH", payload: Date.now() });

        // Cache the data
        saveConversationsToCache(conversations);
      } catch (error) {
        if (abortController.signal.aborted) return;

        const errorObj =
          error instanceof Error
            ? error
            : new Error("Failed to fetch conversations");
        dispatch({ type: "SET_CONVERSATIONS_ERROR", payload: errorObj });

        toast({
          title: "Error fetching conversations",
          description: errorObj.message,
          variant: "destructive",
        });
      } finally {
        if (!abortController.signal.aborted) {
          dispatch({ type: "SET_LOADING_CONVERSATIONS", payload: false });
        }
        abortControllersRef.current.delete(fetchKey);
      }
    },
    [state.organizationId, toast],
  );

  // Fetch conversation details with caching
  const fetchConversationDetails = useCallback(
    async (conversationId: string, useCache: boolean = true): Promise<void> => {
      // Check cache first if enabled
      if (useCache && state.conversationDetailsCache[conversationId]) {
        const cachedData = state.conversationDetailsCache[conversationId];

        // Check if cache is fresh (within 5 minutes)
        if (!isDataStale(cachedData.timestamp, 5 * 60 * 1000)) {
          dispatch({
            type: "LOAD_CACHED_CONVERSATION_DETAILS",
            payload: {
              conversation: cachedData.conversation,
              messages: cachedData.messages,
              formFields: cachedData.formFields,
            },
          });
          return;
        }
      }

      const abortController = new AbortController();
      const fetchKey = `conversation-${conversationId}-${Date.now()}`;
      abortControllersRef.current.set(fetchKey, abortController);

      try {
        dispatch({ type: "SET_LOADING_CONVERSATION_DETAILS", payload: true });
        dispatch({ type: "SET_CONVERSATION_DETAILS_ERROR", payload: null });

        const { data, error } =
          await inboxService.getConversationDetails(conversationId);

        if (abortController.signal.aborted) return;

        if (error) throw error;

        if (data) {
          // Merge userInfo into conversation object for consistent UI display
          const enrichedConversation = {
            ...data.conversation,
            user_name:
              data.userInfo?.name ||
              data.conversation.user_name ||
              "Anonymous User",
            user_email:
              data.userInfo?.email || data.conversation.user_email || null,
            user_phone:
              data.userInfo?.phone || data.conversation.user_phone || null,
          };

          dispatch({
            type: "SET_SELECTED_CONVERSATION",
            payload: enrichedConversation,
          });
          dispatch({ type: "SET_SELECTED_MESSAGES", payload: data.messages });
          dispatch({
            type: "SET_SELECTED_FORM_FIELDS",
            payload: data.formFields,
          });
          dispatch({
            type: "CACHE_CONVERSATION_DETAILS",
            payload: {
              conversationId: conversationId,
              conversation: enrichedConversation,
              messages: data.messages,
              formFields: data.formFields,
            },
          });
        }
      } catch (error) {
        if (abortController.signal.aborted) return;

        const errorObj =
          error instanceof Error
            ? error
            : new Error("Failed to fetch conversation details");
        dispatch({ type: "SET_CONVERSATION_DETAILS_ERROR", payload: errorObj });

        toast({
          title: "Error fetching conversation details",
          description: errorObj.message,
          variant: "destructive",
        });
      } finally {
        if (!abortController.signal.aborted) {
          dispatch({
            type: "SET_LOADING_CONVERSATION_DETAILS",
            payload: false,
          });
        }
        abortControllersRef.current.delete(fetchKey);
      }
    },
    [state.conversationDetailsCache, toast],
  );

  // Auto-fetch conversations when organization changes
  useEffect(() => {
    if (state.isInitialized && state.organizationId) {
      fetchConversations();
    }
  }, [state.isInitialized, state.organizationId, fetchConversations]);

  // Real-time subscriptions
  useEffect(() => {
    if (!state.organizationId || !state.isInitialized) return;

    const channels: any[] = [];

    // First, get all agent IDs for this organization to listen for their conversations
    const setupConversationSubscription = async () => {
      try {
        // Fetch all agent IDs for this organization
        const { data: agentsData, error } = await supabase
          .from("agents")
          .select("id")
          .eq("organization_id", state.organizationId);

        if (error) {
          console.error(
            "Error fetching agents for real-time subscription:",
            error,
          );
          return;
        }

        const agentIds = agentsData?.map((agent) => agent.id) || [];

        if (agentIds.length === 0) {
          return;
        }

        // Subscribe to conversations changes for ALL organization agents
        const conversationsChannel = supabase
          .channel(`inbox-conversations-${state.organizationId}`)
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "conversations",
              filter: `agent_id=in.(${agentIds.map((id) => `"${id}"`).join(",")})`,
            },
            (payload) => {
              if (payload.eventType === "INSERT") {
                const newConversation = payload.new as Conversation;
                dispatch({
                  type: "ADD_CONVERSATION",
                  payload: newConversation,
                });
              } else if (payload.eventType === "UPDATE") {
                const updatedConversation = payload.new as Conversation;
                dispatch({
                  type: "UPDATE_CONVERSATION",
                  payload: updatedConversation,
                });
              } else if (payload.eventType === "DELETE") {
                const deletedId = payload.old.id as string;
                dispatch({ type: "DELETE_CONVERSATION", payload: deletedId });
              }
            },
          )
          .subscribe();

        channels.push(conversationsChannel);
      } catch (error) {
        console.error("Error setting up conversation subscription:", error);
      }
    };

    setupConversationSubscription();

    // Subscribe to messages for selected conversation
    if (state.selectedConversation?.id) {
      const messagesChannel = supabase
        .channel(`inbox-messages-${state.selectedConversation.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "messages",
            filter: `conversation_id=eq.${state.selectedConversation.id}`,
          },
          (payload) => {
            if (payload.eventType === "INSERT") {
              const newMessage = payload.new as Message;
              dispatch({ type: "ADD_MESSAGE", payload: newMessage });
            } else if (payload.eventType === "UPDATE") {
              const updatedMessage = payload.new as Message;
              dispatch({ type: "UPDATE_MESSAGE", payload: updatedMessage });
            }
          },
        )
        .subscribe();

      channels.push(messagesChannel);

      // Subscribe to conversation field values for selected conversation
      const fieldValuesChannel = supabase
        .channel(`inbox-field-values-${state.selectedConversation.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "conversation_field_values",
            filter: `conversation_id=eq.${state.selectedConversation.id}`,
          },
          (payload) => {
            // Refresh conversation details when field values change
            if (
              payload.eventType === "INSERT" ||
              payload.eventType === "UPDATE"
            ) {
              fetchConversationDetails(state.selectedConversation.id);
            }
          },
        )
        .subscribe();

      channels.push(fieldValuesChannel);
    }

    // Subscribe to agent_chat_users changes for this organization
    const agentChatUsersChannel = supabase
      .channel(`inbox-chat-users-${state.organizationId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "agent_chat_users",
        },
        (payload) => {
          // Refresh conversations when user info changes
          if (payload.eventType === "UPDATE") {
            fetchConversations(false);
          }
        },
      )
      .subscribe();

    channels.push(agentChatUsersChannel);

    return () => {
      channels.forEach((channel) => supabase.removeChannel(channel));
    };
  }, [
    state.organizationId,
    state.isInitialized,
    state.selectedConversation?.id,
    fetchConversations,
    fetchConversationDetails,
  ]);

  // Action creators
  const refreshConversations = useCallback(async () => {
    await fetchConversations(false);
  }, [fetchConversations]);

  const refreshConversationDetails = useCallback(
    async (conversationId: string) => {
      await fetchConversationDetails(conversationId);
    },
    [fetchConversationDetails],
  );

  const selectConversation = useCallback(
    (conversationId: string | null) => {
      if (conversationId) {
        const conversation = state.conversations.find(
          (c) => c.id === conversationId,
        );
        if (conversation) {
          dispatch({
            type: "SET_SELECTED_CONVERSATION",
            payload: conversation,
          });

          // Check if we have cached data for this conversation
          if (state.conversationDetailsCache[conversationId]) {
            const cachedData = state.conversationDetailsCache[conversationId];

            // If cache is fresh, use it immediately
            if (!isDataStale(cachedData.timestamp, 5 * 60 * 1000)) {
              dispatch({
                type: "LOAD_CACHED_CONVERSATION_DETAILS",
                payload: {
                  conversation: cachedData.conversation,
                  messages: cachedData.messages,
                  formFields: cachedData.formFields,
                },
              });
              return;
            }
          }

          // Otherwise fetch from server
          fetchConversationDetails(conversationId);
        }
      } else {
        dispatch({ type: "SET_SELECTED_CONVERSATION", payload: null });
        dispatch({ type: "SET_SELECTED_MESSAGES", payload: [] });
        dispatch({ type: "SET_SELECTED_FORM_FIELDS", payload: [] });
      }
    },
    [
      state.conversations,
      state.conversationDetailsCache,
      fetchConversationDetails,
    ],
  );

  const updateConversationStatus = useCallback(
    async (conversationId: string, status: string) => {
      try {
        // Optimistic update
        dispatch({
          type: "UPDATE_CONVERSATION",
          payload: { id: conversationId, status },
        });

        // Make API call
        const { error } = await supabase
          .from("conversations")
          .update({ status })
          .eq("id", conversationId);

        if (error) throw error;
      } catch (error) {
        // Revert optimistic update on error
        const conversation = state.conversations.find(
          (c) => c.id === conversationId,
        );
        if (conversation) {
          dispatch({
            type: "UPDATE_CONVERSATION",
            payload: { id: conversationId, status: conversation.status },
          });
        }

        toast({
          title: "Error updating conversation",
          description: error instanceof Error ? error.message : "Unknown error",
          variant: "destructive",
        });
      }
    },
    [state.conversations, toast],
  );

  const invalidateConversations = useCallback(() => {
    dispatch({ type: "SET_CONVERSATIONS_LAST_FETCH", payload: null });
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
  }, []);

  const invalidateConversationDetails = useCallback(
    (conversationId: string) => {
      dispatch({
        type: "CACHE_CONVERSATION_DETAILS",
        payload: {
          conversationId,
          conversation:
            state.conversationDetailsCache[conversationId]?.conversation || {},
          messages: [],
          formFields: [],
        },
      });
    },
    [state.conversationDetailsCache],
  );

  const invalidateAll = useCallback(() => {
    invalidateConversations();
  }, [invalidateConversations]);

  const reset = useCallback(() => {
    cleanup();
    dispatch({ type: "RESET" });
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.removeItem(CACHE_TIMESTAMP_KEY);
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Memoized context value
  const contextValue = useMemo(
    (): InboxContextType => ({
      ...state,
      refreshConversations,
      refreshConversationDetails,
      selectConversation,
      updateConversationStatus,
      invalidateConversations,
      invalidateConversationDetails,
      invalidateAll,
      reset,
      hasError: !!(state.conversationsError || state.conversationDetailsError),
      isLoading:
        state.isLoadingConversations || state.isLoadingConversationDetails,
    }),
    [
      state,
      refreshConversations,
      refreshConversationDetails,
      selectConversation,
      updateConversationStatus,
      invalidateConversations,
      invalidateConversationDetails,
      invalidateAll,
      reset,
    ],
  );

  return (
    <InboxContext.Provider value={contextValue}>
      {children}
    </InboxContext.Provider>
  );
}

// Hook to use the context
export function useInbox(): InboxContextType {
  const context = useContext(InboxContext);
  if (context === undefined) {
    throw new Error("useInbox must be used within an InboxProvider");
  }
  return context;
}

// Selective hooks for performance
export function useInboxConversations() {
  const {
    conversations,
    isLoadingConversations,
    conversationsError,
    refreshConversations,
  } = useInbox();

  return useMemo(
    () => ({
      conversations,
      isLoading: isLoadingConversations,
      error: conversationsError,
      refresh: refreshConversations,
    }),
    [
      conversations,
      isLoadingConversations,
      conversationsError,
      refreshConversations,
    ],
  );
}

export function useInboxConversationDetails() {
  const {
    selectedConversation,
    selectedMessages,
    selectedFormFields,
    isLoadingConversationDetails,
    conversationDetailsError,
    refreshConversationDetails,
    selectConversation,
    updateConversationStatus,
  } = useInbox();

  return useMemo(
    () => ({
      conversation: selectedConversation,
      messages: selectedMessages,
      formFields: selectedFormFields,
      isLoading: isLoadingConversationDetails,
      error: conversationDetailsError,
      refresh: refreshConversationDetails,
      select: selectConversation,
      updateStatus: updateConversationStatus,
    }),
    [
      selectedConversation,
      selectedMessages,
      selectedFormFields,
      isLoadingConversationDetails,
      conversationDetailsError,
      refreshConversationDetails,
      selectConversation,
      updateConversationStatus,
    ],
  );
}
